/**
 * Growth Agent — Phase 2 surfaces.
 *   /grow/audiences                — Audience & Prospect builder (opt-in gated)
 *   /grow/channels/:businessId     — WhatsApp control panel + Portal keys + Autopilot config
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, Users, Plus, MessageSquare, Smartphone, ShieldCheck, Zap, Pause, Play,
  Monitor, KeyRound, Copy, AlertTriangle, CheckCircle2, Send, Eye, Ban, X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ children, active }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-6">
          <Link to="/grow" className="font-heading font-extrabold text-slate-900 text-lg">
            <span className="text-emerald-600">●</span> WebsiteTech <span className="text-slate-400 font-semibold">Growth Agent</span>
          </Link>
          <nav className="ml-auto flex items-center gap-5 flex-wrap">
            {[
              {to: "/grow", k: "overview", l: "Overview"},
              {to: "/grow/audiences", k: "audiences", l: "Audiences"},
              {to: "/grow/approvals", k: "approvals", l: "Approvals"},
              {to: "/grow/leads", k: "leads", l: "Leads"},
            ].map((n) => (
              <Link key={n.k} to={n.to} className={`text-sm ${active === n.k ? "text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"}`}>{n.l}</Link>
            ))}
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

// =============================================================================
// /grow/audiences
// =============================================================================
export function GrowAudiences() {
  const [businessId, setBusinessId] = useState(localStorage.getItem("grow.lastBusinessId") || "");
  const [list, setList] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [opened, setOpened] = useState(null); // audience id → open roster view

  const load = async (bid) => {
    if (!bid) { setList([]); return; }
    try {
      const { data } = await axios.get(`${API}/grow/audiences?business_id=${bid}`);
      setList(data.items);
    } catch { setList([]); }
  };
  useEffect(() => { load(businessId); }, [businessId]);

  return (
    <Shell active="audiences">
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1">
              <h1 className="font-heading font-extrabold text-slate-900 text-3xl">Audiences</h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">Build first-party, <span className="font-bold">opt-in only</span> B2B audiences. Cold scraping is blocked at the API level — every prospect requires a consent record.</p>
            </div>
            <button onClick={() => setShowNew(true)} data-testid="audience-new" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-xl text-sm inline-flex items-center gap-2">
              <Plus size={14} /> New audience
            </button>
          </div>

          <div className="mt-4 flex gap-2 items-center">
            <label className="text-xs text-slate-500">Business ID</label>
            <input value={businessId} onChange={(e) => { setBusinessId(e.target.value); localStorage.setItem("grow.lastBusinessId", e.target.value); }} data-testid="audience-business-id" placeholder="paste business_id from /grow/dashboard URL" className="border border-slate-200 rounded px-2 py-1 text-xs w-96" />
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audiences-list">
            {list === null && <div className="col-span-2 text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>}
            {list && list.length === 0 && (
              <div className="col-span-2 bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
                <Users size={28} className="mx-auto text-slate-300" />
                <h3 className="font-heading font-bold text-slate-900 mt-3">No audiences yet</h3>
                <p className="text-sm text-slate-500 mt-1">Create your first opt-in B2B audience to run a compliant campaign.</p>
              </div>
            )}
            {list && list.map((a) => (
              <div key={a.id} data-testid={`audience-${a.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><Users size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900">{a.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.icp || "No ICP set"}</p>
                    <div className="flex gap-2 mt-2 text-[10px] text-slate-400">
                      <span>{a.geo || "—"}</span>·<span>{a.industry || "—"}</span>·<span className="text-emerald-700 font-bold">{a.size} opt-in</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => setOpened(a)} data-testid={`audience-open-${a.id}`} className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><Eye size={12} /> Roster</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {showNew && <AudienceNewModal businessId={businessId} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(businessId); }} />}
      {opened && <AudienceRosterModal audience={opened} onClose={() => setOpened(null)} />}
    </Shell>
  );
}

function AudienceNewModal({ businessId, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", icp: "", geo: "", industry: "", size_band: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!businessId) { toast.error("Set a Business ID first"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/grow/audiences`, { business_id: businessId, ...f });
      toast.success("Audience created");
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-lg w-full p-6 relative" data-testid="audience-new-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400"><X size={18} /></button>
        <h3 className="font-heading font-extrabold text-slate-900 text-xl">New audience</h3>
        <div className="mt-5 space-y-3">
          <Input label="Audience name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required testId="aud-name" />
          <Input label="ICP (who's the buyer?)" value={f.icp} onChange={(v) => setF({ ...f, icp: v })} placeholder="Head of Procurement at 100-1000 CPG brands" testId="aud-icp" />
          <div className="grid grid-cols-3 gap-3">
            <Input label="Geography" value={f.geo} onChange={(v) => setF({ ...f, geo: v })} placeholder="India" />
            <Input label="Industry" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} placeholder="CPG" />
            <Input label="Size band" value={f.size_band} onChange={(v) => setF({ ...f, size_band: v })} placeholder="100-1000" />
          </div>
        </div>
        <button disabled={busy} type="submit" data-testid="aud-submit" className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl text-sm inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create audience
        </button>
      </form>
    </div>
  );
}

function AudienceRosterModal({ audience, onClose }) {
  const [prospects, setProspects] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/grow/audiences/${audience.id}/prospects`); setProspects(data.items); } catch { setProspects([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [audience.id]);
  const enrich = async () => {
    setEnriching(true);
    try { const { data } = await axios.post(`${API}/grow/audiences/${audience.id}/enrich`); toast.success(`${data.updated} prospects enriched (${data.provider})`); await load(); }
    catch { toast.error("Enrichment failed"); }
    setEnriching(false);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 relative max-h-[85vh] overflow-y-auto" data-testid="audience-roster-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400"><X size={18} /></button>
        <h3 className="font-heading font-extrabold text-slate-900 text-xl">{audience.name}</h3>
        <p className="text-xs text-slate-500 mt-1">{audience.icp || "No ICP"} · {audience.size} opt-in prospects</p>
        <div className="mt-4 flex gap-2">
          <button onClick={() => setShowAdd(true)} data-testid="roster-add" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><Plus size={12} /> Add opt-in contact</button>
          <button onClick={enrich} disabled={enriching} data-testid="roster-enrich" className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1">
            {enriching ? <Loader2 className="animate-spin" size={12} /> : <Zap size={12} />} Enrich (mock)
          </button>
        </div>
        <div className="mt-5 space-y-2" data-testid="roster-list">
          {prospects === null && <div className="text-center py-6 text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>}
          {prospects && prospects.length === 0 && <div className="text-center text-slate-500 text-sm py-6">No contacts yet. Add your first opt-in contact.</div>}
          {prospects && prospects.map((p) => (
            <div key={p.id} data-testid={`prospect-${p.id}`} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex gap-3 items-center text-sm">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0">{(p.first_name[0] || "?") + (p.last_name[0] || "")}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{p.first_name} {p.last_name} <span className="font-normal text-slate-500">· {p.title || "—"}</span></div>
                <div className="text-xs text-slate-500 truncate">{p.company || "—"} · {p.email || p.phone} · <span className="text-emerald-700 font-bold">opt-in from {p.source}</span>{p.enriched && <span className="ml-1 text-amber-700 bg-amber-100 px-1 rounded">enriched</span>}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showAdd && <ProspectAddModal audienceId={audience.id} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function ProspectAddModal({ audienceId, onClose, onAdded }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", title: "", source: "web-form", opt_in: false, consent_text: "I agree to receive marketing communications from this business." });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.opt_in) { toast.error("Opt-in checkbox is required"); return; }
    setBusy(true);
    try { await axios.post(`${API}/grow/audiences/add-prospect`, { audience_id: audienceId, ...f }); toast.success("Prospect added"); onAdded(); }
    catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-lg w-full p-6 relative" data-testid="prospect-add-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400"><X size={18} /></button>
        <h3 className="font-heading font-extrabold text-slate-900 text-xl">Add opt-in contact</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Input label="First name" value={f.first_name} onChange={(v) => setF({ ...f, first_name: v })} required testId="pros-first" />
          <Input label="Last name" value={f.last_name} onChange={(v) => setF({ ...f, last_name: v })} />
          <Input label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} type="email" testId="pros-email" />
          <Input label="Phone (E.164)" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} placeholder="+91…" />
          <Input label="Company" value={f.company} onChange={(v) => setF({ ...f, company: v })} />
          <Input label="Title" value={f.title} onChange={(v) => setF({ ...f, title: v })} />
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Opt-in source</label>
            <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5">
              <option value="web-form">Web form</option>
              <option value="event">Event / trade-show</option>
              <option value="referral">Customer referral</option>
              <option value="existing-customer">Existing customer</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Consent text shown to them</label>
            <textarea rows={2} value={f.consent_text} onChange={(e) => setF({ ...f, consent_text: e.target.value })} data-testid="pros-consent" className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs mt-0.5" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="pros-opt-in" checked={f.opt_in} onChange={(e) => setF({ ...f, opt_in: e.target.checked })} />
            This contact gave consent to receive marketing communications
          </label>
        </div>
        <button disabled={busy} type="submit" data-testid="pros-submit" className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl text-sm inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Add
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "", required = false, testId = "" }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:border-emerald-500 outline-none" />
    </div>
  );
}

// =============================================================================
// /grow/channels/:businessId — WhatsApp + Portal + Autopilot
// =============================================================================
export function GrowChannels() {
  const { businessId } = useParams();
  const [tab, setTab] = useState("whatsapp");
  return (
    <Shell active="channels">
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-extrabold text-slate-900 text-3xl">Channels & Autopilot</h1>
          <p className="text-sm text-slate-500 mt-1">Business ID: <code className="text-xs">{businessId}</code></p>
          <div className="mt-6 flex gap-2 border-b border-slate-200">
            {[
              {k:"whatsapp",l:"WhatsApp",i:Smartphone},
              {k:"portal",l:"Portal Ad API",i:Monitor},
              {k:"autopilot",l:"Autopilot",i:ShieldCheck},
            ].map((t) => {
              const I=t.i;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`channels-tab-${t.k}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                  <I size={14} /> {t.l}
                </button>
              );
            })}
          </div>
          <div className="mt-6">
            {tab === "whatsapp" && <WhatsAppPanel businessId={businessId} />}
            {tab === "portal" && <PortalPanel businessId={businessId} />}
            {tab === "autopilot" && <AutopilotPanel businessId={businessId} />}
          </div>
        </div>
      </section>
    </Shell>
  );
}

function WhatsAppPanel({ businessId }) {
  const [phone, setPhone] = useState("+919999988888");
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/grow/whatsapp/messages?business_id=${businessId}&phone_e164=${encodeURIComponent(phone)}`); setMessages(data.items); } catch {}
  };
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [businessId, phone]);

  const optIn = async () => {
    setBusy(true);
    try { await axios.post(`${API}/grow/whatsapp/opt-in`, { business_id: businessId, phone_e164: phone, source: "demo", consent_text: "I agree to receive WhatsApp marketing updates" }); toast.success("Opt-in recorded"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } setBusy(false);
  };
  const sendTemplate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/grow/whatsapp/send`, { business_id: businessId, phone_e164: phone, kind: "template", template_name: "welcome", params: ["WebsiteTech"] }); await load(); toast.success("Template sent"); } catch (e) { toast.error(e?.response?.data?.detail || "Blocked"); } setBusy(false);
  };
  const sendText = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/grow/whatsapp/send`, { business_id: businessId, phone_e164: phone, kind: "text", body }); setBody(""); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Blocked"); } setBusy(false);
  };
  const simulateInbound = async (text) => {
    try { await axios.post(`${API}/grow/whatsapp/demo-inbound`, { business_id: businessId, phone_e164: phone, body: text }); await load(); toast.success(`Simulated inbound: "${text}"`); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="wa-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-heading font-extrabold text-slate-900">Send & simulate</h3>
        <p className="text-xs text-slate-500 mt-1">Demo adapter (<span className="font-bold">fake</span>) — swap for Meta Cloud API / Twilio / 360dialog in production.</p>
        <div className="mt-4">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Lead phone (E.164)</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="wa-phone" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5" />
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <button onClick={optIn} disabled={busy} data-testid="wa-opt-in" className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><CheckCircle2 size={12} /> Record opt-in</button>
          <button onClick={sendTemplate} disabled={busy} data-testid="wa-send-template" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><Send size={12} /> Send template</button>
          <button onClick={() => simulateInbound("Yes interested")} disabled={busy} data-testid="wa-sim-reply" className="bg-white border border-slate-300 hover:border-blue-500 text-slate-700 font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><MessageSquare size={12} /> Simulate reply</button>
          <button onClick={() => simulateInbound("STOP")} disabled={busy} data-testid="wa-sim-stop" className="bg-white border border-slate-300 hover:border-rose-500 text-slate-700 hover:text-rose-700 font-bold px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1"><Ban size={12} /> Simulate STOP</button>
        </div>
        <div className="mt-4 flex gap-2">
          <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply within 24h window…" onKeyDown={(e) => e.key === "Enter" && sendText()} data-testid="wa-body" className="flex-1 border border-slate-200 rounded px-2 py-2 text-sm" />
          <button onClick={sendText} disabled={busy} data-testid="wa-send-text" className="bg-slate-700 hover:bg-slate-900 text-white font-bold px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1"><Send size={12} /> Send</button>
        </div>
      </div>
      <div className="bg-slate-900 text-white rounded-2xl p-5 flex flex-col max-h-[520px]">
        <div className="flex items-center gap-2"><Smartphone size={16} /><h3 className="font-heading font-extrabold">Thread · {phone}</h3></div>
        <div className="mt-4 flex-1 overflow-y-auto space-y-2 pr-1" data-testid="wa-thread">
          {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No messages yet. Opt-in + send a template to start.</p>}
          {[...messages].reverse().map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.direction === "outbound" ? "bg-emerald-600 ml-auto" : "bg-white/10 border border-white/20"}`} data-testid={`wa-msg-${m.id}`}>
              <div>{m.text}</div>
              <div className="text-[9px] text-white/60 mt-1">{m.state} · {m.kind}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PortalPanel({ businessId }) {
  const [portalId, setPortalId] = useState("socialpulse-demo");
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState({});
  const [issuing, setIssuing] = useState(false);

  const loadStats = async () => {
    try { const { data } = await axios.get(`${API}/grow/portal/stats?business_id=${businessId}`); setStats(data.stats); } catch {}
  };
  useEffect(() => { loadStats(); }, [businessId]); // eslint-disable-line

  const issue = async () => {
    setIssuing(true);
    try {
      const { data } = await axios.post(`${API}/grow/portal/issue-token`, { business_id: businessId, portal_id: portalId });
      setTokens([{ portal_id: data.portal_id, token: data.token }, ...tokens]);
      toast.success("Portal token issued");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setIssuing(false);
  };

  return (
    <div className="space-y-5" data-testid="portal-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-heading font-extrabold text-slate-900">Issue a portal token</h3>
        <p className="text-xs text-slate-500 mt-1">SocialPulse-network portals use this token in <code>X-Portal-Token</code> header to fetch eligible campaigns and report impressions.</p>
        <div className="mt-4 flex gap-2">
          <input value={portalId} onChange={(e) => setPortalId(e.target.value)} data-testid="portal-id" placeholder="socialpulse-mumbai" className="flex-1 border border-slate-200 rounded px-2 py-2 text-sm" />
          <button onClick={issue} disabled={issuing} data-testid="portal-issue" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1">
            {issuing ? <Loader2 className="animate-spin" size={14} /> : <KeyRound size={14} />} Issue token
          </button>
        </div>
        {tokens.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="portal-tokens">
            {tokens.map((t, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500">{t.portal_id}</span>
                <code className="flex-1 text-[11px] font-mono text-slate-800 truncate">{t.token}</code>
                <button onClick={() => { navigator.clipboard.writeText(t.token); toast.success("Copied"); }} className="text-slate-400 hover:text-slate-700"><Copy size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-heading font-extrabold text-slate-900">Portal performance</h3>
        {Object.keys(stats).length === 0 ? (
          <p className="text-xs text-slate-500 mt-2">No portal events yet. Call <code>GET /api/grow/portal/eligible-campaigns</code> and <code>POST /api/grow/portal/{"{impression|click|conversion}"}</code> from your portal with the token above.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="portal-stats">
            {Object.entries(stats).map(([p, s]) => (
              <div key={p} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-xs font-bold text-slate-500">{p}</div>
                <div className="mt-2 text-sm text-slate-800">
                  <span className="font-bold">{s.impression || 0}</span> imp · <span className="font-bold">{s.click || 0}</span> clk · <span className="font-bold">{s.conversion || 0}</span> conv
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AutopilotPanel({ businessId }) {
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/grow/autopilot/${businessId}`); setPolicy(data.policy); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const upgrade = async () => {
    setBusy(true);
    try { await axios.post(`${API}/grow/autopilot/upgrade`, { business_id: businessId, level: 2 }); toast.success("Upgraded to Level 2 Guarded Automation"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const stop = async () => {
    setBusy(true);
    try { await axios.post(`${API}/grow/autopilot/emergency-stop?business_id=${businessId}`); toast.success("Emergency stop active"); await load(); } catch { toast.error("Failed"); }
    setBusy(false);
  };
  const resume = async () => {
    setBusy(true);
    try { await axios.post(`${API}/grow/autopilot/resume?business_id=${businessId}`); toast.success("Resumed"); await load(); } catch { toast.error("Failed"); }
    setBusy(false);
  };

  if (!policy) return <div className="text-center py-10"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="autopilot-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1">
            <h3 className="font-heading font-extrabold text-slate-900">Current autonomy level</h3>
            <div className="mt-2 inline-flex items-center gap-2 text-sm font-bold" data-testid="autopilot-level">
              <ShieldCheck size={16} className="text-emerald-600" />
              Level {policy.level} — {policy.level === 1 ? "Draft (every outbound requires approval)" : "Guarded Automation (per-type caps enforced)"}
              {policy.emergency_stop && <span className="text-rose-700 bg-rose-100 px-2 py-0.5 rounded text-xs">● STOPPED</span>}
            </div>
          </div>
          {policy.level === 1 && (
            <button onClick={upgrade} disabled={busy} data-testid="autopilot-upgrade" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Upgrade to Level 2
            </button>
          )}
          {policy.level === 2 && !policy.emergency_stop && (
            <button onClick={stop} disabled={busy} data-testid="autopilot-stop" className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              <Pause size={14} /> Emergency stop
            </button>
          )}
          {policy.level === 2 && policy.emergency_stop && (
            <button onClick={resume} disabled={busy} data-testid="autopilot-resume" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              <Play size={14} /> Resume
            </button>
          )}
        </div>
      </div>
      {policy.level === 2 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-heading font-extrabold text-slate-900">Daily caps</h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="autopilot-caps">
            {Object.entries(policy.caps || {}).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{k.replace(/_/g, " ")}</div>
                <div className="mt-2 text-sm text-slate-800"><span className="font-bold">{v.volume_per_day}</span> sends/day</div>
                <div className="text-sm text-slate-800">Budget <span className="font-bold">${v.budget_per_day_usd}</span>/day</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800">
          Level 3 "Controlled Autopilot" requires enterprise onboarding and a signed autopilot policy. Contact sales to enable it for this account.
        </div>
      </div>
    </div>
  );
}
